import { LightIcon, PlusIcon } from "@xola/icons";
import { Alert, Breadcrumb, Button, ButtonGroup, HeaderToolbar } from "@xola/ui-kit";
import { hasDistributionProfileConfigured, SecondaryButton } from "common";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Permission, PurchaseRuleLink, XolaPartner, XolaPartnerStatus } from "types";
import { AddOperatorDrawer } from "./AddOperatorDrawer";
import { CreatePartnerConnectionModal } from "./CreatePartnerConnectionModal";
import { EditPartnerConnectionModal } from "./EditPartnerConnectionModal";
import { PartnerFilter, PartnerList } from "./PartnerList";
import { ApplyPartnerTermsTemplateModal } from "./TermsTemplate/ApplyTermsPartnerTemplateModal";
import { IsGranted } from "@/components/Common/IsGranted";
import { SearchInput } from "@/components/Common/SearchInput";
import { TableSkeleton } from "@/components/Common/Skeleton/TableSkeleton";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { EmbeddedAppTriggerLocation } from "@/embeddedApps/EmbeddedAppTriggerLocation";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useVisibility } from "@/hooks/useVisibility";
import { Page } from "@/pages/Page";

export const PartnerListPage = () => {
    const { isGranted } = useAuthorization();
    const seller = useSeller();
    const history = useHistory();

    const [search, setSearch] = useState<string>("");
    const [showAddPartnerDrawer, { toggle: toggleShowAddPartnerDrawer }] = useVisibility();
    const [showEditPartnerModal, { toggle: toggleEditPartnerModal }] = useVisibility();
    const [showCreatePartnerModal, { toggle: toggleCreatePartnerModal }] = useVisibility();

    const { data: termsTemplates, isLoading: isLoadingTermsTemplates } = useAutoFetchCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        { "tags._id": "Affiliate" },
    );

    const [selectedPartnerForEdit, setSelectedPartnerForEdit] = useState<XolaPartner>();
    const [currentFilter, setCurrentFilter] = useState(PartnerFilter.ALL);
    const [selectedPartners, setSelectedPartners] = useState<ID[]>([]);
    const [isApplyTermsTemplateModalOpen, setIsApplyTermsTemplateModalOpen] = useState(false);
    const showConfigureDistributionProfile = !hasDistributionProfileConfigured(seller);
    const showConfigureTermsTemplateAlert = termsTemplates.length === 0;
    const showPartnerList =
        !isLoadingTermsTemplates && !showConfigureDistributionProfile && !showConfigureTermsTemplateAlert;
    const showAddOperatorButton = isGranted(Permission.OPERATORS_ACTIONS);

    const redirectToDistributionProfile = () => {
        history.push("/distribution/profile");
    };

    const redirectToCreateTermsTemplatePage = () => {
        history.push("/distribution/termsTemplates/create");
    };

    const handleSearchChange = (searchResult: string) => {
        setSearch(searchResult.trim());
    };

    const handlePartnerSelection = (selectedPartners: string[]) => {
        setSelectedPartners(selectedPartners);
    };

    const handlePartnerFilterChange = (value: number) => {
        setCurrentFilter(value);
        setSelectedPartners([]);
    };

    const handlePartnerClick = (partner: XolaPartner) => {
        setSelectedPartnerForEdit(partner);
        if (partner.status === XolaPartnerStatus.DISCONNECTED) {
            toggleCreatePartnerModal();
        } else {
            toggleEditPartnerModal();
        }
    };

    return (
        <Page title="Partners List">
            <ColumnLayout>
                <HeaderToolbar classNames="justify-between">
                    <HeaderToolbar.Breadcrumb>
                        <Breadcrumb.Item>Distribution</Breadcrumb.Item>
                        <Breadcrumb.Item>Operators</Breadcrumb.Item>
                    </HeaderToolbar.Breadcrumb>
                    <EmbeddedAppTriggerLocation id="distribution.operators.headerBar" />
                    <div className="space-x-2">
                        {showConfigureDistributionProfile ? null : (
                            <IsGranted permissions={Permission.OPERATORS_ACTIONS}>
                                <SecondaryButton onClick={redirectToDistributionProfile}>
                                    Distribution Profile
                                </SecondaryButton>
                            </IsGranted>
                        )}

                        {showPartnerList && showAddOperatorButton ? (
                            <Button icon={<PlusIcon />} onClick={toggleShowAddPartnerDrawer}>
                                Add Operator
                            </Button>
                        ) : null}
                    </div>
                </HeaderToolbar>
                <ColumnLayout.Fill>
                    <ContentLayout isWide>
                        {isLoadingTermsTemplates ? (
                            <TableSkeleton rows={7} columns={5} />
                        ) : (
                            <div className="space-y-5">
                                <div className="flex items-center space-x-10">
                                    <div className="flex flex-grow items-center space-x-4">
                                        <h2>Manage Operators</h2>
                                        {showPartnerList ? (
                                            <>
                                                <SearchInput
                                                    className="flex-grow"
                                                    value={search}
                                                    onChange={handleSearchChange}
                                                />
                                                <Button
                                                    disabled={selectedPartners.length === 0}
                                                    onClick={() => setIsApplyTermsTemplateModalOpen(true)}
                                                >
                                                    Apply Terms Template
                                                </Button>
                                            </>
                                        ) : null}
                                    </div>
                                    {showPartnerList ? (
                                        <PartnerListFilter value={currentFilter} onChange={handlePartnerFilterChange} />
                                    ) : null}
                                </div>
                                {showConfigureDistributionProfile ? (
                                    <ConfigureDistributionProfileAlert onClick={redirectToDistributionProfile} />
                                ) : null}

                                {showConfigureTermsTemplateAlert ? (
                                    <ConfigureTermsTemplateAlert onClick={redirectToCreateTermsTemplatePage} />
                                ) : null}

                                {showPartnerList ? (
                                    <PartnerList
                                        search={search}
                                        currentFilter={currentFilter}
                                        handlePartnerSelection={handlePartnerSelection}
                                        onPartnerClick={handlePartnerClick}
                                    />
                                ) : null}
                            </div>
                        )}

                        <ApplyPartnerTermsTemplateModal
                            isOpen={isApplyTermsTemplateModalOpen}
                            partners={selectedPartners}
                            onClose={() => setIsApplyTermsTemplateModalOpen(false)}
                        />
                        <AddOperatorDrawer isOpen={showAddPartnerDrawer} onClose={toggleShowAddPartnerDrawer} />
                        {selectedPartnerForEdit && showEditPartnerModal ? (
                            <EditPartnerConnectionModal
                                isOpen={showEditPartnerModal}
                                partner={selectedPartnerForEdit}
                                onClose={toggleEditPartnerModal}
                            />
                        ) : null}
                        {showCreatePartnerModal && selectedPartnerForEdit ? (
                            <CreatePartnerConnectionModal
                                isOpen={showCreatePartnerModal}
                                partnerSeller={selectedPartnerForEdit?.partnerSeller}
                                onClose={toggleCreatePartnerModal}
                            />
                        ) : null}
                    </ContentLayout>
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};

interface PartnerListFilterProps {
    value: number;
    onChange: (value: number) => void;
}

const PartnerListFilter = ({ value, onChange }: PartnerListFilterProps) => {
    return (
        <ButtonGroup size="medium" value={value} onChange={onChange}>
            <ButtonGroup.Button>All</ButtonGroup.Button>
            <ButtonGroup.Button>Connected</ButtonGroup.Button>
            <ButtonGroup.Button>Pending</ButtonGroup.Button>
            <ButtonGroup.Button>Disconnected</ButtonGroup.Button>
        </ButtonGroup>
    );
};

const ConfigureDistributionProfileAlert = ({ onClick }: { onClick: () => void }) => {
    return (
        <Alert color="primary">
            <div className="flex items-center p-2">
                <div className="ml-2 flex-1" data-testid="">
                    <div className="mb-3 flex space-x-2">
                        <LightIcon size="medium" />
                        <span className="text-md font-bold">Distribution Profile Not Configured</span>
                    </div>
                    <span className="text-base">
                        Please configure your distribution profile to find and connect with partners
                    </span>
                </div>
                <div className="flex justify-end">
                    <Button onClick={onClick}>Setup Distribution Profile</Button>
                </div>
            </div>
        </Alert>
    );
};

const ConfigureTermsTemplateAlert = ({ onClick }: { onClick: () => void }) => {
    return (
        <Alert color="primary">
            <div className="flex items-center p-2">
                <div className="ml-2 flex-1" data-testid="">
                    <div className="mb-3 flex space-x-2">
                        <LightIcon size="medium" />
                        <span className="text-md font-bold">No Terms Templates Configured</span>
                    </div>
                    <span className="text-base">
                        You currently do not have any Terms Templates defined. Let&apos;s start by creating a new Terms
                        Template
                    </span>
                </div>
                <div className="flex justify-end">
                    <Button onClick={onClick}>Create Template</Button>
                </div>
            </div>
        </Alert>
    );
};
